import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';

/**
 * QuestionEditPage (refactor)
 * - Robust data-load with abort, normalized shapes
 * - Multiselect states dropdown (outside-click close)
 * - Media: preview, upload, remove (all wired up)
 * - Safer validation and number coercion
 * - Minimal inline styles; accessible labels
 */
export default function QuestionEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [question, setQuestion] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [states, setStates] = useState([]);
  const [mediaPreview, setMediaPreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  // --- UI: states dropdown ---
  const [statesOpen, setStatesOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(e.target)) setStatesOpen(false);
    };
    document.addEventListener('click', onDocClick, true);
    return () => document.removeEventListener('click', onDocClick, true);
  }, []);

  // --- Load question, categories, states ---
  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      try {
        setIsLoading(true);
        setError('');

        const [qRes, cRes, sRes] = await Promise.all([
          api.get(`/admin/questions/${id}`, { signal: controller.signal }),
          api.get('/categories', { signal: controller.signal }),
          api.get('/states', { signal: controller.signal }),
        ]);

        const q = qRes?.data ?? {};

        // Normalize state_ids
        let normalizedStateIds = [];
        if (Array.isArray(q.state_ids)) {
          normalizedStateIds = q.state_ids.map(Number).filter(Number.isFinite);
        } else if (q.state_ids != null) {
          normalizedStateIds = String(q.state_ids)
            .split(',')
            .map((v) => Number(v.trim()))
            .filter(Number.isFinite);
        } else if (q.state_id != null) {
          normalizedStateIds = [Number(q.state_id)].filter(Number.isFinite);
        }

        const nextQuestion = {
          question_text: q.question_text ?? '',
          choice_a: q.choice_a ?? '',
          choice_b: q.choice_b ?? '',
          choice_c: q.choice_c ?? '',
          choice_d: q.choice_d ?? '',
          choice_e: q.choice_e ?? '',
          correct_choice: q.correct_choice ?? 'A',
          rationale: q.rationale ?? '',
          category_id: q.category_id ?? '',
          state_ids: normalizedStateIds,
          media_url: q.media_url ?? '',
          media_type: q.media_type ?? null,
        };

        setQuestion(nextQuestion);
        setCategories(Array.isArray(cRes?.data) ? cRes.data : []);
        setStates(Array.isArray(sRes?.data) ? sRes.data : []);
        setMediaPreview(nextQuestion.media_url);
      } catch (err) {
        if (err?.name === 'CanceledError' || err?.message === 'canceled') return;
        console.error('[QuestionEdit] load failed:', err);
        setError('Failed to load question, states, or categories.');
      } finally {
        setIsLoading(false);
      }
    })();

    return () => controller.abort();
  }, [id]);

  // --- Change handlers ---
  const handleChange = (e) => {
    const { name, value } = e.target;
    setQuestion((prev) => {
      if (!prev) return prev;
      // Coerce category_id to number when possible
      const nextVal = name === 'category_id' ? (value === '' ? '' : Number(value)) : value;
      return { ...prev, [name]: nextVal };
    });
  };

  const toggleState = (stateId) => {
    setQuestion((prev) => {
      if (!prev) return prev;
      const arr = Array.isArray(prev.state_ids) ? prev.state_ids : [];
      const next = arr.includes(stateId) ? arr.filter((id) => id !== stateId) : [...arr, stateId];
      return { ...prev, state_ids: next };
    });
  };

  // --- Media upload ---
  const handleMediaChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setError('');
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/admin/questions/${id}/media`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const url = res?.data?.media_url ?? '';
      const type = res?.data?.media_type ?? null;
      setMediaPreview(url);
      setQuestion((prev) => (prev ? { ...prev, media_url: url, media_type: type } : prev));
      setMsg('Media uploaded.');
    } catch (err) {
      console.error('[QuestionEdit] media upload failed:', err);
      setError(err?.response?.data?.message || 'Media upload failed.');
    } finally {
      // Reset input so the same file can be chosen again if needed
      e.target.value = '';
    }
  };

  const handleRemoveMedia = async () => {
    if (!window.confirm('Remove the currently attached media?')) return;
    try {
      setError('');
      await api.delete(`/admin/questions/${id}/media`);
      setMediaPreview('');
      setQuestion((prev) => (prev ? { ...prev, media_url: '', media_type: null } : prev));
      setMsg('Media removed.');
    } catch (err) {
      console.error('[QuestionEdit] media remove failed:', err);
      setError('Failed to remove media.');
    }
  };

  // --- Save/submit ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!question) return;
    setSaving(true);
    setError('');
    setMsg('');

    const {
      question_text, choice_a, choice_b, choice_c, choice_d, choice_e,
      correct_choice, rationale, category_id, state_ids
    } = question;

    // Basic validation
    const requiredFilled = question_text && choice_a && choice_b && correct_choice && category_id !== '' && Array.isArray(state_ids) && state_ids.length > 0;
    if (!requiredFilled) {
      setError('Question text, at least Choices A and B, correct choice, category, and one or more states are required.');
      setSaving(false);
      return;
    }

    // Correct choice must refer to a non-empty choice
    const letter = String(correct_choice).toUpperCase();
    const map = { A: choice_a, B: choice_b, C: choice_c, D: choice_d, E: choice_e };
    if (!map[letter]) {
      setError(`Correct choice is ${letter}, but Choice ${letter} is empty.`);
      setSaving(false);
      return;
    }

    const payload = {
      question_text,
      choice_a,
      choice_b,
      ...(choice_c ? { choice_c } : {}),
      ...(choice_d ? { choice_d } : {}),
      ...(choice_e ? { choice_e } : {}),
      correct_choice: letter,
      rationale,
      category_id: Number(category_id),
      state_ids: state_ids.map(Number),
      // media handled by its own endpoints
    };

    try {
      await api.put(`/admin/questions/${id}`, payload);
      setMsg('Question updated successfully.');
      navigate('/admin/reports');
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to update question.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <p>Loading question...</p>
      </div>
    );
  }
  if (!question) {
    return (
      <div className="page-container">
        <p>{error || 'Question not found.'}</p>
      </div>
    );
  }

  // Helper: selected labels for compact chip display
  const selectedStates = (question.state_ids || [])
    .map((sid) => states.find((s) => s.state_id === sid))
    .filter(Boolean);

  return (
    <div className="page-container question-edit-container">
      <h2 style={{ marginBottom: 12 }}>
        Edit Question <span style={{ fontWeight: 400, color: '#888' }}>(ID: {id})</span>
      </h2>

      <Link
        to="/admin/reports"
        style={{ color: '#267C2D', fontWeight: 500, fontSize: '0.98em', textDecoration: 'underline', marginBottom: 12, display: 'inline-block' }}
      >
        ← Back to Flagged Reports
      </Link>

      <form className="question-edit-form" onSubmit={handleSubmit} style={{ maxWidth: 720, margin: 'auto' }}>
        {/* Question */}
        <div className="form-group">
          <label htmlFor="question_text">Question Text</label>
          <textarea
            id="question_text"
            name="question_text"
            value={question.question_text || ''}
            onChange={handleChange}
            rows={3}
            required
          />
        </div>

        {/* States + Category */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          {/* STATES: Multiselect dropdown */}
          <div className="form-group" ref={dropdownRef}>
            <label>States</label>
            <button
              type="button"
              className="link-btn"
              onClick={() => setStatesOpen((o) => !o)}
              aria-expanded={statesOpen}
              aria-haspopup="listbox"
              style={{
                width: '100%',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                border: '1px solid var(--border)', borderRadius: 'var(--input-radius)',
                padding: '10px 12px', background: 'var(--bg-alt)',
              }}
            >
              <span style={{ color: selectedStates.length ? 'inherit' : 'var(--text-soft)' }}>
                {selectedStates.length
                  ? selectedStates.map((s) => s.state_name || s.state_abbr).join(', ')
                  : 'Select one or more states'}
              </span>
              <span aria-hidden>▾</span>
            </button>

            {statesOpen && (
              <div
                className="dropdown-content"
                role="listbox"
                style={{
                  marginTop: 6,
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  background: 'var(--bg)',
                  maxHeight: 280,
                  overflowY: 'auto',
                  boxShadow: 'var(--shadow)'
                }}
              >
                {states.map((st) => (
                  <label key={st.state_id} className="profile-states-dropdown" style={{ padding: '6px 4px' }}>
                    <input
                      type="checkbox"
                      checked={!!question.state_ids?.includes(st.state_id)}
                      onChange={() => toggleState(st.state_id)}
                    />
                    <span>{st.state_abbr || st.state_name}</span>
                  </label>
                ))}
              </div>
            )}
            <small className="cat-reset-hint">You must pick at least one state.</small>
          </div>

          {/* CATEGORY */}
          <div className="form-group">
            <label htmlFor="category_id">Category</label>
            <select id="category_id" name="category_id" value={question.category_id ?? ''} onChange={handleChange} required>
              <option value="" disabled>-- Select Category --</option>
              {categories.map((cat) => (
                <option key={cat.category_id} value={cat.category_id}>
                  {cat.category_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Answers */}
        <fieldset style={{ marginTop: 10, marginBottom: 8, padding: '14px', borderRadius: 9, border: '1.5px solid var(--border)', background: 'var(--bg-alt)' }}>
          <legend style={{ padding: '0 8px', fontWeight: 600, color: '#267C2D', fontSize: '1.02em' }}>Answer Choices</legend>
          {['A', 'B', 'C', 'D', 'E'].map((letter, idx) => (
            <div key={letter} className="form-group" style={{ marginBottom: idx < 4 ? 10 : 0 }}>
              <label htmlFor={`choice_${letter.toLowerCase()}`}>
                {`Choice ${letter}${letter === 'A' || letter === 'B' ? '' : ' (optional)'}:`}
              </label>
              <input
                id={`choice_${letter.toLowerCase()}`}
                type="text"
                name={`choice_${letter.toLowerCase()}`}
                value={question[`choice_${letter.toLowerCase()}`] || ''}
                onChange={handleChange}
                required={letter === 'A' || letter === 'B'}
              />
            </div>
          ))}
        </fieldset>

        {/* Correct Choice + Rationale */}
        <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 14 }}>
          <div className="form-group">
            <label htmlFor="correct_choice">Correct Choice</label>
            <select id="correct_choice" name="correct_choice" value={question.correct_choice || 'A'} onChange={handleChange} required>
              {['A', 'B', 'C', 'D', 'E'].map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="rationale">Rationale</label>
            <textarea id="rationale" name="rationale" value={question.rationale || ''} onChange={handleChange} rows={2} required />
          </div>
        </div>

        {/* Media */}
        <div className="form-group">
          <label>Media (optional)</label>

          {mediaPreview ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <img
                src={mediaPreview}
                alt="Attached media preview"
                className="question-edit-media-preview"
                style={{ maxWidth: 240, maxHeight: 160, objectFit: 'contain', border: '1px solid var(--border)', borderRadius: 6, padding: 4 }}
              />
              <button type="button" className="link-btn" onClick={handleRemoveMedia}>
                Remove media
              </button>
            </div>
          ) : (
            <input type="file" accept="image/*,video/*,audio/*" onChange={handleMediaChange} />
          )}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
          <button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button>
          <Link to="/admin/reports" className="link-btn" style={{ padding: '8px 10px' }}>Cancel</Link>
          {msg && <span className="profile-msg" role="status">{msg}</span>}
          {error && (
            <span className="profile-msg" style={{ color: '#b32222', background: '#fdeeee', borderLeft: '4px solid #e23b3b' }}>{error}</span>
          )}
        </div>
      </form>
    </div>
  );
}
